import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import html2pdf from "html2pdf.js";
import { getToken } from "../../../utils/tokenService";
import { Capacitor } from "@capacitor/core";
import { savePdfBlob } from "../../../utils/nativePdfSave";
import {
  completeDownloadNotification,
  failDownloadNotification,
  startDownloadNotification,
} from "../../../utils/downloadNotification";
import "../../../pages/User/OfferLetter/style.css";

const DEFAULT_SCALE = 1;
const MIN_SCALE = 0.6;
const MAX_SCALE = 1.6;
const MM_TO_PX = 3.78;
const A4_WIDTH = 210;

const calculateScale = () => {
  if (typeof window === "undefined") return DEFAULT_SCALE;
  const availableWidth = Math.max(window.innerWidth - 64, 320);
  const a4WidthPx = A4_WIDTH * MM_TO_PX;
  return Math.min(DEFAULT_SCALE, Math.max(MIN_SCALE, availableWidth / a4WidthPx));
};

const ClientShowOfferLetter = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [offerLetter, setOfferLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [downloading, setDownloading] = useState(false);
  const previewRef = useRef(null);
  const letterKind = location.pathname.includes("/appointment-letter")
    ? "appointment"
    : "offer";
  const apiPath =
    letterKind === "appointment" ? "appointment-letters" : "offer-letters";
  const listPath =
    letterKind === "appointment"
      ? "/client_dashboard/appointment-letter"
      : "/client_dashboard/offer-letter";
  const heading = letterKind === "appointment" ? "Appointment Letter" : "Offer Letter";

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const token = await getToken();
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/client/${apiPath}/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.data?.success) {
          setOfferLetter(response.data.offerLetter);
        } else {
          throw new Error(response.data?.message || "Invalid response");
        }
      } catch (err) {
        console.error("Client offer letter fetch error:", err);
        const message =
          err.response?.data?.message ||
          "Unable to load offer letter. Please try again.";
        setError(message);
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      load();
    } else {
      setError("Invalid offer letter id.");
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const handleResize = () => {
      setScale(calculateScale());
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const formatCompanyName = (name) =>
    String(name || "")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\s+/g, " ")
      .trim();

  const isLegacyAppointmentContent = (value) => {
    const body = String(value || "").replace(/<[^>]*>/g, " ").toLowerCase();
    return /subject\s*:\s*appointment/.test(body) ||
      /appointment\s+for\s+the\s+position/.test(body) ||
      /pleased\s+to\s+inform\s+you\s+that\s+you\s+have\s+been\s+appointed/.test(body) ||
      /your\s+employment\s+will\s+be\s+subject\s+to\s+the\s+following/.test(body);
  };

  const correctedOfferContent = (contentValue, letter) => {
    if (letterKind === "appointment" || !isLegacyAppointmentContent(contentValue)) {
      return contentValue;
    }

    const companyName =
      formatCompanyName(letter?.letterContent?.companyInfo?.name) || "our organization";
    const employeeName = letter?.userId?.full_Name || "Employee";
    const position = letter?.hiringId?.job_category || letter?.hiringId?.jobCategory || "Employee";

    return `Date: ${new Date().toLocaleDateString()}\n\nTo,\n${employeeName}\n\nSubject: Offer of Employment - ${position}\n\nDear ${employeeName},\n\nWe are pleased to offer you the position of ${position} with ${companyName}. This offer is subject to completion of our standard joining formalities.\n\nYour role, reporting arrangements, work schedule, compensation, and other employment terms will be shared with you as part of your employment documentation. Please review this offer carefully and contact us if you need any clarification.\n\nTo accept this offer, please sign and return the letter by the requested date. We look forward to welcoming you to ${companyName}.\n\nSincerely,\nFor ${companyName}\nAuthorized Signatory`;
  };

  const renderHTML = (content) => {
    if (!content) return null;
    return <div dangerouslySetInnerHTML={{ __html: String(content).replace(/\n/g, "<br />") }} />;
  };

  const downloadPDF = async () => {
    if (!previewRef.current || !offerLetter) return;
    setDownloading(true);
    const documentName = `${letterKind === "appointment" ? "Appointment" : "Offer"} letter`;
    const notificationId = startDownloadNotification(documentName);
    try {
      const element = previewRef.current;
      const images = element.querySelectorAll("img");
      await Promise.all(
        Array.from(images).map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete) return resolve();
              img.onload = img.onerror = resolve;
            })
        )
      );

      const exportNode = element.cloneNode(true);
      const exportHost = document.createElement("div");
      exportHost.className = "pdf-export-host";
      exportHost.style.position = "fixed";
      exportHost.style.left = "-9999px";
      exportHost.appendChild(exportNode);
      document.body.appendChild(exportHost);

      const filename = `${letterKind}-letter-${offerLetter._id}.pdf`;
      const worker = html2pdf()
        .set({
          margin: [0, 0, 0, 0],
          filename,
          image: { type: "jpeg", quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .from(exportNode);

        if (Capacitor.isNativePlatform()) {
          const blob = await worker.outputPdf("blob");
          const saved = await savePdfBlob(blob, filename);
          if (!saved?.success) throw new Error("PDF could not be saved");
          completeDownloadNotification(notificationId, documentName, { onClick: saved.open });
        } else {
          await worker.save();
          completeDownloadNotification(notificationId, documentName);
        }

        document.body.removeChild(exportHost);
    } catch (err) {
      console.error("Offer letter download error:", err);
      failDownloadNotification(notificationId, documentName);
      const stale = document.querySelector(".pdf-export-host");
      if (stale?.parentNode) stale.parentNode.removeChild(stale);
    } finally {
      setDownloading(false);
    }
  };

  const ZoomControls = () => (
    <div className="zoom-controls">
      <button onClick={() => setScale((prev) => Math.min(MAX_SCALE, prev + 0.1))}>+</button>
      <button onClick={() => setScale((prev) => Math.max(MIN_SCALE, prev - 0.1))}>-</button>
      <button onClick={() => setScale(calculateScale())}>Reset</button>
    </div>
  );

  if (loading) {
    return (
      <div className="container py-4">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-2">Loading offer letter...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">{error}</div>
        <button className="btn btn-outline-secondary" onClick={() => navigate("/client_dashboard/offer-letter")}>
          Back to list
        </button>
      </div>
    );
  }

  if (!offerLetter) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">Offer letter not found.</div>
        <button className="btn btn-outline-secondary" onClick={() => navigate("/client_dashboard/offer-letter")}>
          Back to list
        </button>
      </div>
    );
  }

  const { letterContent = {}, documentStyle = {}, assets = {}, status } = offerLetter;
  const companyInfo = letterContent.companyInfo || {};
  const documentTitle = letterContent.title || heading;
  const bodyContent = correctedOfferContent(letterContent.content, offerLetter);

  return (
    <div className="container py-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
            <div>
              <h3 className="mb-1">{heading} Preview</h3>
              <p className="mb-0 text-muted">{companyInfo.name}</p>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(listPath)}>
                Back to list
              </button>
          <button className="btn btn-primary btn-sm" onClick={downloadPDF} disabled={downloading}>
            {downloading ? "Generating PDF..." : "Download PDF"}
          </button>
        </div>
      </div>
      {status && (
        <div className="mb-2">
          <span className="badge bg-info text-uppercase">{status}</span>
        </div>
      )}
      <div className="offer-letter-preview">
        <div
          ref={previewRef}
          className="a4-page"
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "top center",
            width: "210mm",
            height: "297mm",
            background: "#fff",
            padding: "24px",
            boxShadow: "0 0 20px rgba(0,0,0,0.1)",
          }}
        >
          <div className="header d-flex justify-content-between align-items-center">
            {assets.logo && (
              <div
                className="logo-container-user"
                style={{
                  width: documentStyle.logoSize?.width || 100,
                  marginRight: "20px",
                }}
              >
                <img
                  crossOrigin="anonymous"
                  src={`${process.env.REACT_APP_BACKEND_URL}${assets.logo}`}
                  alt="Company logo"
                  className="logo"
                />
              </div>
            )}
            <div className={`company-details ${!assets.logo ? "w-100" : ""}`}>
              <h5 className="company-name text-end">{formatCompanyName(companyInfo.name)}</h5>
              <div className="text-end">{companyInfo.address}</div>
              <div className="contact-info text-end text-muted">
                Email: {companyInfo.email || "-"}
                {companyInfo.phone && <span className="ms-1">| PH NO-{companyInfo.phone}</span>}
              </div>
            </div>
          </div>
          <hr className="divider" />
          <div className="letter-title text-center my-4">
            <h2 className="text-decoration-underline">{documentTitle}</h2>
          </div>
          <div
            className="letter-body"
            style={{
              fontSize: documentStyle.fontSize || "16px",
              fontFamily: documentStyle.fontFamily || "Arial, sans-serif",
              whiteSpace: "pre-line",
            }}
          >
            {renderHTML(bodyContent)}
          </div>
        </div>
        <ZoomControls />
      </div>
    </div>
  );
};

export default ClientShowOfferLetter;
